<template>
  <EditionModal
    :is-displayed="inPopup || id !== null"
    :query-param="actionCreationQueryParam"
    class="action-creation"
    :not-as-page="inPopup"
  >
    <FiltersZone no-options-button>
      <div slot="shortcuts">
        <div class="v-centerer">
          <Switcher v-model="mode" :titles="availableModes" />
          <Pdca
            v-if="id > 0"
            v-model="action.pdca"
            v-quick-title="pdcaDisabled ? $t('message.PDCA_MUST_BE_IN_PLAN') : undefined"
            class="action-creation__pdca-wrapper"
            :class="{ 'action-creation__pdca-wrapper--disabled': pdcaDisabled }"
            :pdca-id="action.id"
            :disabled="pdcaDisabled"
            :quick-action="Number(action.isQuickAction) === 1"
          />
        </div>
      </div>
    </FiltersZone>
    <div class="f-h-below-filters">

      <!-- Whole page wrapper -->
      <div class="action-creation__wrapper">

        <!-- Main page content -->
        <div class="action-creation__content-wrapper">

          <!-- First column -->
          <div class="action-creation__column">
            <EditableLabel
              ref="description"
              :key="action.id"
              v-model="action.name"
              class="action-creation__description"
              :legend="$t('message.DESCRIPTION')"
              :placeholder="$t('message.DESCRIPTION_PLACEHOLDER')"
              :disabled="noEdition"
              use-textarea
              @input="editAction('name')"
            />

            <ImpactSelector
              v-model="action.impact"
              :legend="$t('message.IMPACT')"
              class="action-creation__impact"
              :disabled="noEdition"
              :min="$store.state.device.isSmartphonePortrait"
              @input="editAction('impact')"
            />

            <div class="action-creation__leader">
              <div class="legend legend--bottom-margin">
                {{ $t('message.LEADER') }}
              </div>
              <div class="v-centerer">
                <UserThumbnail
                  :value="action.leader"
                  :all-users="allUsers"
                  :disabled="true"
                  fetch-data
                  @click.native="$refs.leaderEdition.edit()"
                />
                <EditableLabelRadio
                  ref="leaderEdition"
                  v-model="leaderValue"
                  class="action-creation__leader__button"
                  :items="allUsers"
                  id-key="radioId"
                  label-key="label"
                  :placeholder="$t('message.NO_LEADER')"
                  :disabled="noEdition"
                  generic-border
                />
              </div>
            </div>

            <div class="action-creation__deadline">
              <div class="legend legend--bottom-margin">
                {{ $t('message.DEADLINE') }}
              </div>
              <DateField
                :value="action.deadline"
                :placeholder="$t('message.DEADLINE')"
                @input="onDeadlineEdit"
              />
              <div v-if="originalDeadline" class="action-creation__deadline__original">
                {{
                  $t('message.ORIGINAL_DEADLINE')
                    + $t('message.TWO_DOTS')
                    + $moment(originalDeadline).format($t('longFullDateFormat'))
                }}
              </div>
            </div>

            <div v-if="!action.isSecuring" class="action-creation__plans">
              <div class="legend legend--bottom-margin">
                {{ $t('message.ACTION_PLAN') }}
              </div>

              <Dropdown class="f-w generic-border--thin-border" :closed-text="currentPlan ? currentPlan.name : ''">
                <RadioGroup
                  v-model="action.plan"
                  :items="allPlans"
                  :disabled="noEdition"
                  @input="assignAction"
                />
              </Dropdown>
            </div>

            <div v-if="action.state === 2" style="font-style: italic;font-weight: 600;">
              {{ $t('message.ACTION_COMPLETED') }}
            </div>

            <IntCheckbox
              v-model="action.isSecuring"
              class="action-creation__is-securing"
              :class="fieldsClasses"
              :title="$t('message.SECURING_ACTION')"
              :disabled="noEdition"
              @input="editAction('isSecuring')"
            />

          </div>
          <!-- END First column -->

          <!-- Second column -->
          <div class="action-creation__column">

            <EditableLabelRadio
              v-model="action.indicators"
              class="action-creation__indicators"
              :legend="$t('message.INDICATORS')"
              :items="indicatorsArray"
              label-key="name"
              :disabled="noEdition"
              :placeholder="$t('message.CLICK_TO_CHANGE')"
              multiple
              generic-border
              @input="editAction('indicators')"
            />

            <div class="action-creation__problem">
              <div class="legend legend--bottom-margin">
                {{ $t('message.PROBLEM') }}
              </div>
              <div
                class="generic-border--thin-border generic-border--clickable"
                @click="assigningToProblem = true"
              >
                <div>{{ action.problemId > 0 ? action.problemTitle : $t('message.AC_NOT_LINKED_PB') }}</div>
              </div>
            </div>

            <RouterLink
              v-if="action.problemId > 0"
              :to="getPbSheetLink(action.problemId)"
              class="generic-border generic-border--text-btn generic-border--gray-border action-creation__problem-link"
            >
              <Icon>{{ eyeBold }}</Icon>
              <span>{{ $t('message.PROBLEM_SHEET') }}</span>
            </RouterLink>

            <EditableLabelRadio
              v-model="action.cause"
              class="action-creation__causes"
              :legend="$t('message.CAUSE')"
              :items="allCauses"
              label-key="text"
              :placeholder="$t('message.CLICK_TO_CHANGE')"
              :disabled="noEdition"
              generic-border
              @input="editAction('cause')"
            />

            <EditableLabel
              ref="comments"
              v-model="action.comment"
              class="action-creation__comments"
              :legend="$t('message.COMMENTS')"
              :placeholder="$t('message.COMMENT_PLACEHOLDER')"
              :disabled="noEdition"
              use-tiptap-editor
              display-tiptap-menu
              display-tiptap-tasks
              @input="editAction('comment')"
            />

          </div>
          <!-- END Second column -->

          <!-- Third column -->
          <div class="action-creation__column">
            <div class="action-creation__files-wrapper">
              <div class="legend legend--bottom-margin">
                {{ $t('message.FILES') }}
              </div>
              <FileChipList
                v-model="action.files"
                :editable="!noEdition"
                column
                upload-zone
                pathname="actions/"
                own-file-viewer
                display-files
                class="action-creation__files"
                @input="editAction('files')"
              />

              <button
                class="generic-border generic-border--text-btn generic-border--blue-border action-creation__hist-btn"
                @click="isHistoryDisplayed = true"
              >
                {{ $t("message.HISTORY") }}
              </button>

            </div>
          </div>
          <!-- END Third column -->

        </div>
        <!-- END Main page content -->

      </div>
      <!-- END Whole page wrapper -->

      <div class="action-creation__buttons v-centerer">
        <button
          class="generic-border generic-border--text-btn generic-border--clickable generic-border--red"
          :disabled="noEdition"
          @click="confirmDeleteAction"
        >
          {{ $t("message.DELETE") }}
        </button>

        <div class="v-centerer action-creation__buttons__save">
          <button
            v-if="action.state !== 2"
            class="generic-border generic-border--text-btn generic-border--clickable generic-border--blue-border"
            @click="complete"
          >
            <Icon>{{ archivesBox }}</Icon>
            <span>{{ $t('message.COMPLETE_ACTION') }}</span>
          </button>

          <button
            v-else
            class="generic-border generic-border--text-btn generic-border--clickable generic-border--blue-border"
            @click="uncomplete"
          >
            <Icon>{{ cancelMedium }}</Icon>
            <span>{{ $t('message.RESTORE_ACTION') }}</span>
          </button>

          <button
            class="generic-border generic-border--text-btn generic-border--clickable generic-border--blue"
            @click="saveAndQuit"
          >
            {{ $t("message.SAVE") }}
          </button>

        </div>
      </div>
    </div>

    <!-- Hidden components -->
    <!-- Problem field edition -->
    <Modal v-model="assigningToProblem" class="action-creation__problems-selection" xlarge>
      <div class="problems-list">
        <h2 class="basic-sub-title">
          {{ $t('message.PICK_A_PB') }}
        </h2>
        <Problems
          as-component
          no-route-link
          no-menu
          hide-header
          @itemClick="assignToProblem"
        />
      </div>
    </Modal>

    <!-- Team picker when sending an action to a support plan -->
    <AdvancedTeamPicker
      v-if="chooseTeam"
      :value="[]"
      display-all-teams
      invisible
      ok-button
      single-value
      edit-mode-always-on
      @input="suppTeamClicked"
      @endEdit="suppTeamClicked(null)"
    />

    <!-- Enter a postponement reason -->
    <Modal v-model="enteringPostponementReason">
      <div class="postponement-reason h-v-centerer">
        <div class="postponement-reason__content">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="h-v-centerer mb5em" v-html="$t('message.STATE_POSTPONEMENT_REASON')" />
          <EditableLabel
            ref="postponementLabel"
            v-model="postponementReason"
            class="postponement-reason__content__label"
            :placeholder="$t('message.REASON_PH')"
            use-textarea
            :textarea-edition-timeout="0"
          />
          <div class="postponement-reason__content__list">
            <div
              v-for="(reason, idx) in filteredPostpmtReasons"
              :key="idx"
              class="postponement-reason__content__list__item"
              :class="postpmtReasonItemClass(reason)"
              @click="postpmtReasonClicked(reason)"
            >
              {{ reason }}
            </div>
            <div v-if="!filteredPostpmtReasons.length" class="postponement-reason__content__list__item">
              {{ $t('message.POSTPONEMENT_REASON_NO_PREV') }}
            </div>
          </div>
          <div class="postponement-reason__buttons h-v-centerer">
            <button
              class="generic-border generic-border--text-btn generic-border--blue-border"
              :disabled="postponementReason.length === 0"
              @click="postponementReasonEntered"
            >
              {{ $t('message.DONE') }}
            </button>
          </div>
        </div>
      </div>
    </Modal>

    <Modal v-model="isHistoryDisplayed" @input="isHistoryDisplayed = false">
      <div class="action-creation__history">
        <!-- Support related information -->
        <!-- Leader history -->
        <div v-if="leadHistoryItems && leadHistoryItems.length" class="action-creation__history__leader">
          <h3>
            {{ $t('message.ACTION_LEADER_HISTORY') }}
          </h3>
          <div
            v-for="item in leadHistoryItems"
            :key="item.key"
            class="action-creation__history__item history-item"
          >
            <div class="date">
              {{ item.mDate.format($t('fullDateTimeFormat')) }}
            </div>
            <!-- Action sent as a support request -->
            <div v-if="item.support">
              {{ $t('message.ACTION_ASSIGNED_TEAMS', item) }}
            </div>
            <!-- Action creation -->
            <div v-else-if="item.type === 1">
              {{ $t('message.ACTION_CREATED_BY', item) }}
            </div>
            <!-- Action returned after a support request -->
            <div v-else-if="item.returned">
              {{ $t('message.ACTION_RETURNED_TEAMS', item) }}
            </div>
            <!-- Leader edition -->
            <div v-else>
              {{ $t('message.ACTION_LEADER_CHANGED', item) }}
            </div>
          </div>
          <!-- Return action to sender -->
          <button
            v-if="isSupportAction && currentTeam.id === action.affectTeam"
            class="generic-border generic-border--text-btn generic-border--blue-border mt10em"
            @click="returnAction()"
          >
            {{ $t("message.RETURN_ACTION") }}
          </button>
        </div>

        <div v-if="leadHistoryItems && leadHistoryItems.length" class="sep sep--full-width" />
        <!-- Postponements history -->
        <div
          v-if="postponements.length && currentTeam.forceActionPostponementReason"
          class="postponement-history"
        >
          <h3>
            {{ $t('message.POSTPONEMENTS_HISTORY') }}
          </h3>
          <HistoryItem
            v-for="item in postponements"
            :key="item.id"
            :item="item"
            translation-suffix="ACTION"
          >
            <div class="postponement-history__reason">
              <EditableLabel
                :value="item.comment"
                :legend="`${$t('message.REASON')}${$t('message.TWO_DOTS')}`"
                :placeholder="$t('message.NO_POSTPONEMENT_REASON')"
                disabled
              />
            </div>
          </HistoryItem>
        </div>
        <!-- End of support related information -->
      </div>
    </Modal>
  </EditionModal>
</template>

<script>
/**
 * Action edition/creation modal
 *
 * @vue-prop {Number} [propId] - When the action creation is used as a sub-component,
 *                               you must specify the id of the action here
 * @vue-prop {Boolean} [inPopup] - Set to true to use the action creation as a sub-component
 */

import { cloneDeep, uniq } from 'lodash';
// Vuex
import { createNamespacedHelpers, mapState } from 'vuex';
import toastr from 'toastr';
import {
  FETCH_PENDING_ACTIONS_NUMBER,
  FETCH_TEAMS,
  FETCH_ACTION_PLANS,
  FETCH_INDICATORS_ONLY,
  ASSIGN_ACTION,
  RETURN_ACTION,
  FETCH_ACTION_POSTPMT_REASONS,
} from '@/store/action-types';
import { GET_ACTION_PLANS, GET_ACTION_POSTPMT_REASONS } from '@/store/mutation-types';
import {
  DELETE_ACTION,
  EDIT_ACTION,
  FETCH_SINGLE_PROBLEM,
  FETCH_SINGLE_ACTION,
} from '@/store/modules/problem/action-types';
// Import components
import AdvancedTeamPicker from '@/components/advanced-team-picker.vue';
import DateField from '@/components/date-field.vue';
import Dropdown from '@/components/dropdown.vue';
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import EditionModal from '@/components/edition-modals/edition-modal.vue';
import FileChipList from '@/components/file-system/file-chip-list.vue';
import FiltersZone from '@/components/filters-zone.vue';
import HistoryItem from '@/components/history-item.vue';
import Icon from '@/components/icon.vue';
import ImpactSelector from '@/components/impact-selector.vue';
import IntCheckbox from '@/components/int-checkbox.vue';
import Modal from '@/components/modal.vue';
import Pdca from '@/components/pdca.vue';
import Problems from '@/views/dmp/problems.vue';
import RadioGroup from '@/components/radio-group.vue';
import Switcher from '@/components/switcher.vue';
import UserThumbnail from '@/components/user-thumbnail.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import IAHelpStep from '@/utils/classes/IAHelpStep';
import { calert, decodeHTML, nestedFlatten } from '@/utils/main';
import {
  archivesBox,
  cancelMedium,
  eyeBold,
  securing,
} from '@/assets/icons/';
import { securingIconClass } from '@/utils/problem-utils';
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';
import { actionCreationQueryParam } from '@/utils/navigation-constants';


const { mapState: fileMapState } = createNamespacedHelpers('file');
const emptyAction = {
  id: -1,
  name: '',
  leader: 0,
  impact: 0,
  gain: '',
  gainUnit: '',
  deadline: null,
  comment: '',
  plan: 0,
  affectTeam: 0,
  problemId: 0,
  problemTitle: '',
  causeTitle: '',
  state: 0,
  files: [],
};

function actionCreationInitialData() {
  return {
    isHistoryDisplayed: false,
    saving: false,
    // Action data
    action: Object.assign({}, emptyAction),
    // Other data
    sliderRef: 'sliderRef',
    fromPlan: 0,
    promptBeforeLeave: false,
    // Assign to support team
    chooseTeam: false,
    // Buffer all the team related to this action while staying on this page
    relatedTeams: [],
    // Modal to assign the action to a problem
    assigningToProblem: false,
    // When psotpoing an action, if forceActionPostponementReason is true, enter a reason
    enteringPostponementReason: false,
    postponementReason: '',
    deadlinePendingPostponementReason: null,
    actionDeleted: false,
  };
}

export default {

  name: 'ActionCreationModal',

  components: {
    AdvancedTeamPicker,
    DateField,
    Dropdown,
    EditableLabel,
    EditableLabelRadio,
    EditionModal,
    FileChipList,
    FiltersZone,
    HistoryItem,
    Icon,
    ImpactSelector,
    IntCheckbox,
    Modal,
    Pdca,
    Problems,
    RadioGroup,
    Switcher,
    UserThumbnail,
  },

  mixins: [mixinSocketHandler],

  props: {
    inPopup: Boolean,
    propId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      ...actionCreationInitialData(),
      archivesBox,
      cancelMedium,
      eyeBold,
      securing,
      actionCreationQueryParam,
      availableModes: [
        this.$t('message.PDCA'),
        this.$t('message.QUICK_ACTION'),
      ],
      // Properties used by other components. See src/views/readme.md
      pageBackButtonAction: () => {
        this.quit(this.closeModal);
      },
      iaTeachText: this.$t('sensei.TEACHER_TEXT'),
      iaTeachBtns: [this.teacherBtn('PLAN_ACTION'), this.teacherBtn('SECURING_ACTION')],
    };
  },

  computed: {
    /** Number: ID of the action. If 0, creating a new action */
    id() {
      if (this.inPopup) {
        return this.propId;
      }
      return this.$route.query[actionCreationQueryParam] === undefined
        ? null
        : Number(this.$route.query[actionCreationQueryParam]);
    },
    /** Boolean: Whether the action creation was started because the user was following a meeting */
    followingMeeting() {
      return this.$route.query.followingMeeting && Number(this.$route.query.followingMeeting) === 1;
    },
    /** Number: Edition mode. PDCA (default) or quick action */
    mode: {
      get() {
        return this.action && (this.action.isQuickAction || this.action.isSecuring) ? 1 : 0;
      },
      set(isQuickAction) {
        this.action.isQuickAction = isQuickAction;
        this.editAction('isQuickAction');
      },
    },
    /** True if current user is different from the affected team */
    noEdition() {
      if (this.currentUser && this.currentUser.id === this.action.leader) {
        return false;
      }
      return !this.action.affectTeam || (this.action.affectTeam !== this.currentTeam.id && !this.action.accessGranted);
    },
    /** Boolean: PDCA cannot be edited if the action isn't in a plan (except for securing acitons) */
    pdcaDisabled() {
      return this.noEdition || (!this.isInActionPlan && this.action.isSecuring === 0);
    },
    /** Object: HTML classes applied to fields */
    fieldsClasses() {
      return {
        'clickable-hoverable': !this.noEdition,
      };
    },
    /** True if the current action is in an action plan */
    isInActionPlan() {
      return this.fromPlan && this.fromPlan !== 0;
    },
    displayActionGain() {
      return this.App.isOnDemoMode || (this.currentTeam && this.currentTeam.displayActionGain);
    },
    emptyAction() {
      return { ...emptyAction, affectTeam: this.currentTeam.id };
    },
    /** Array: IDs of all the teams that may be assigned this action */
    allRelatedTeams() {
      if (this.stateProblem) {
        return uniq([...this.relatedTeams, ...(this.stateProblem.accesses || [])]);
      }
      return uniq([...this.relatedTeams]);
    },
    /** Users formatted for EditableLabelRadio */
    allUsers() {
      const { companyTeams } = this.$store.getters;
      const teamItems = [];
      this.allRelatedTeams.forEach((teamId) => {
        const teamObj = companyTeams[`${teamId}`];
        if (teamObj) {
          teamItems.push(teamObj);
        }
      });
      return teamItems.map(team => ({
        ...team,
        title: team.name,
        radioItems: team.users.map(user => ({ ...user, radioId: `${team.id}-${user.id}` })),
      }));
    },
    /** Array: action plans of the current team */
    allPlans() {
      const plans = cloneDeep(this.actionPlans);
      plans.unshift({ id: 0, name: this.$t('message.ACTION_PENDING') });
      for (let i = 0, len = plans.length, p; i < len; i++) {
        p = plans[i];
        p.label = p.name + (i === 0 ? '' : ` (${p.nActions} / ${p.maxActions})`);
      }
      return plans;
    },
    /** Object: Currently selected action plan of this action */
    currentPlan() {
      return this.allPlans.find(plan => plan.id === this.action.plan);
    },
    /** Boolean: Trus if the action has been sent to a team and hasn't been returned yet */
    isSupportAction() {
      return this.action.supportData.some(data => data.returned === null);
    },
    /** Array: History of leader edition and team assignment */
    leadHistoryItems() {
      const items = [];
      if (!this.action.supportData) {
        return items;
      }
      const action = cloneDeep(this.action);
      const actionAuthor = action.history.find(hc => hc.type === 1 && hc.authorFirstName);
      if (actionAuthor) {
        const name = this.$t('fullNameFormat', {
          firstName: decodeHTML(actionAuthor.authorFirstName),
          lastName: decodeHTML(actionAuthor.authorLastName),
        });
        items.push({ ...actionAuthor, name, mDate: this.$moment(actionAuthor.date) });
      }
      action.supportData.forEach((data) => {
        // History item each time the action is sent
        data.key = `support${data.id}`;
        data.mDate = this.$moment(data.created);
        data.fromTeamName = decodeHTML(data.fromTeamName);
        data.toTeamName = decodeHTML(data.toTeamName);
        items.push({ ...data, support: true, dateSortable: data.mDate.format('YYYYMMDDHHmmss') });
        // Add another item in history if the action was returned
        if (data.returned !== null) {
          data.key = `return${data.id}`;
          data.mDate = this.$moment(data.returned);
          items.push({ ...data, return: true, dateSortable: data.mDate.format('YYYYMMDDHHmmss') });
        }
      });
      action.history
        .filter(hc => hc.field === 'leader')
        .forEach((change) => {
          change.key = `leader${change.id}`;
          change.mDate = this.$moment(change.date);
          change.toUser = this.$t('fullNameFormat', {
            firstName: decodeHTML(change.toUserFirstName),
            lastName: decodeHTML(change.toUserLastName),
          });
          change.teamName = decodeHTML(change.teamName);
          change.toTeamName = decodeHTML(change.toTeamName);
          items.push({ ...change, dateSortable: change.mDate.format('YYYYMMDDHHmmss') });
        });
      return items.sort((item1, item2) => item1.dateSortable - item2.dateSortable);
    },
    /** Array: Occurrences of a user postponing this action */
    postponements() {
      return (this.action.history || [])
        .filter((hc) => {
          if (hc.fromValue === null || hc.field !== 'deadline') {
            return false;
          }
          const fromDeadline = this.$moment(hc.fromValue);
          const toDeadline = this.$moment(hc.toValue);
          return fromDeadline.isValid() && toDeadline.isValid() && toDeadline.isAfter(fromDeadline);
        })
        .map(hc => ({ ...hc, mDate: this.$moment(hc.date) }));
    },
    /** Moment: First deadline set for this action */
    originalDeadline() {
      const firstDeadlineChange = (this.action.history || [])
        .find(hc => hc.field === 'deadline' && hc.fromValue === null);
      return firstDeadlineChange ? firstDeadlineChange.toValue : null;
    },
    /** Array: of postponement reasons of the previous actions, filtered using the current reason */
    filteredPostpmtReasons() {
      const terms = this.postponementReason.toUpperCase().split(/[\s,-./']/);
      return cloneDeep(this.allPostpmtReasons).sort((reason1, reason2) => {
        if (!terms.some(term => !reason1.toUpperCase().includes(term))
          && terms.some(term => !reason2.toUpperCase().includes(term))
        ) {
          return -1;
        }
        if (!terms.some(term => !reason2.toUpperCase().includes(term))
          && terms.some(term => !reason1.toUpperCase().includes(term))
        ) {
          return 1;
        }
        return 0;
      });
    },
    /** Object: Action as it was last set in the state */
    stateAction() {
      return this.$store.state.problem.actions[`${this.id}`];
    },
    /** Object: Problem as it was last set in the state */
    stateProblem() {
      return this.$store.state.problem.problems[`${this.action.problem}`];
    },
    /** Array: All the causes of the problem linked to this action if any */
    allCauses() {
      return this.stateProblem
        ? nestedFlatten(this.stateProblem.wCauses || [], 'wCauses')
        : [];
    },
    /** String: Value to use for the leader EditableLabelRadio */
    leaderValue: {
      get() {
        return `${this.action.affectTeam}-${this.action.leader}`;
      },
      set(leaderTeam) {
        const [team, leader] = leaderTeam.split('-');
        if (team && leader) {
          this.action.leader = Number(leader);
          this.action.affectTeam = Number(team);
          this.editAction(['affectTeam', 'leader']);
        }
      },
    },
    ...fileMapState({
      lastFileDeleted: state => state.lastFileDeleted,
    }),
    ...mapState({
      allPostpmtReasons: state => state[GET_ACTION_POSTPMT_REASONS.stateKey] || [],
      allPostpmtReasonsLoading: state => state[GET_ACTION_POSTPMT_REASONS.loadingKey],
      actionPlans: state => state[GET_ACTION_PLANS.stateKey] || [],
      actionPlansLoading: state => state[GET_ACTION_PLANS.loadingKey],
    }),
    indicatorsArray() {
      return (this.$store.state.indicatorsArrayPerTeam[`${this.action.affectTeam}`] || {}).data || [];
    },
    indicatorsArrayLoading() {
      return (this.$store.state.indicatorsArrayPerTeam[`${this.action.affectTeam}`] || {}).loading;
    },
    // Properties used by other components. See src/views/readme.md
    pageTitle() {
      return this.promptBeforeLeave ? this.$t('message.NEW_ACTION') : this.$t('message.ACTION');
    },
  },

  watch: {
    lastFileDeleted(file) {
      if (this.action.files.indexOf(file.id) !== -1) {
        this.refresh();
      }
    },
    id(newVal, oldVal) {
      if (this.id !== null) {
        // Id changes. The page must be refreshed and a new action must be created if id === 0
        this.init();
        // If the old id was 0, this means that the action currently displayed is a new one
        this.promptBeforeLeave = oldVal === 0;
      } else {
        Object.assign(this.$data, actionCreationInitialData());
      }
    },
    stateAction: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal) {
          this.action = cloneDeep(newVal);
          this.fromPlan = newVal.plan;
          // Refresh the data of the problem to assign causes and change assignable teams
          if (newVal.problem > 0 && (!oldVal || newVal.problem !== oldVal.problem)) {
            this.$store.dispatch(`problem/${FETCH_SINGLE_PROBLEM}`, { id: this.action.problem });
          }
        }
      },
    },
  },

  created() {
    if (this.id !== null) {
      this.init();
      this.promptBeforeLeave = this.id === 0;
    }

    this.handleSocketMessage(SocketMessages.CC_MSGS.ACTION_EDITION, (msg) => {
      if (Number(msg.id) !== Number(this.action.id)) return;
      this.refresh();
    });

    if (!this.actionPlansLoading) {
      this.$store.dispatch(FETCH_ACTION_PLANS, { actions: true });
    }
    // Refresh teams for the radio selection in case multiple teams are assignable
    this.$store.dispatch(FETCH_TEAMS);

    // Register the event from the router triggered when the user created a new action while another one is displayed.
    // If the modal is in the process of creating a new action,
    // the user will be prompted to keep/delete the new action or to cancel the navigation
    globalEventDispatcher.register(ShizenGlobalEvent.NEW_MODAL_WHILE_ONE_ACTIVE, (params) => {
      if (this.id !== null) {
        // Handle the event if the modal is currently displayed
        this.quit(params.next);
      }
    });
  },

  methods: {
    decodeHTML,
    securingIconClass,
    /** Close the action edition modal */
    closeModal() {
      if (this.inPopup) {
        this.$emit('close');
      } else {
        this.$router.back();
      }
    },
    /** Try to leave the page */
    quit(next) {
      if (this.promptBeforeLeave) {
        calert(this.$t('message.ACTION_SAVE_BEFORE_LEAVE'), [
          {
            title: this.$t('message.YES_LEAVE_SAVE'),
            action: () => {
              this.promptBeforeLeave = false;
              if (next) {
                next();
              }
            },
            sClass: 'green',
          }, {
            title: this.$t('message.NO_LEAVE_WITHOUT_SAVING'),
            action: () => {
              this.promptBeforeLeave = false;
              this.deleteAction(next);
            },
            sClass: 'red',
          }, {
            title: this.$t('message.CANCEL_NOT_LEAVING'),
          },
        ]);
      } else {
        next();
      }
    },
    /** If the action exists, fetch the action. Otherwise, create a new one */
    init() {
      if (!this.followingMeeting && Number(this.id) === 0) {
        this.action = Object.assign({}, this.emptyAction);
        this.$store.dispatch(FETCH_INDICATORS_ONLY);
        this.save(null, (d) => {
          // this.$router.replace(this.getActionCreationLink(d.id));
          const query = Object.assign({}, this.$route.query);
          query[actionCreationQueryParam] = d.id;
          this.$router.replace({ query });
        });
      } else {
        this.refresh();
        if (this.followingMeeting) {
          const query = { ...Object.assign({}, this.$route.query), followingMeeting: undefined };
          this.$router.replace({ query });
        }
      }
    },
    /** Fetch the action */
    refresh() {
      // set the current team as assignable to this action
      // (if the action doesn't belong to this team, eition will be disabled anyway)
      if (this.currentTeam && this.currentTeam.id && this.allRelatedTeams.indexOf(this.currentTeam.id) === -1) {
        this.relatedTeams.push(this.currentTeam.id);
      }
      this.saving = false;
      this.fromPlan = 0;
      this.$store.dispatch(`problem/${FETCH_SINGLE_ACTION}`, { id: this.id }).then((data) => {
        if (this.allRelatedTeams.indexOf(this.currentTeam.id) === -1) {
          this.relatedTeams.push(this.currentTeam.id);
        }
        if (data.affectTeam !== this.currentTeam.id) {
          if (this.allRelatedTeams.indexOf(data.affectTeam) === -1) {
            this.relatedTeams.push(data.affectTeam);
          }
        }
        // Fetch "linkable" indicators (the indicators of the affected team)
        this.$nextTick(() => {
          if (!this.indicatorsArrayLoading) {
            this.$store.dispatch(FETCH_INDICATORS_ONLY, { team: this.action.affectTeam });
          }
        });
      });
    },
    /** Save the action's edited field */
    editAction(fields, additionalParams = {}) {
      if (this.actionDeleted) {
        return;
      }
      const params = { ...additionalParams, id: this.action.id };
      fields = fields instanceof Array ? fields : [fields];
      fields.forEach((key) => {
        params[key] = this.action[key];
      });
      this.$store.dispatch(`problem/${EDIT_ACTION}`, params).then((response) => {
        const { data } = response;
        if (data instanceof Object) {
          this.action = Object.assign({}, data);
          this.fromPlan = data.plan;
          toastr.remove();
          toastr.success(this.$t('message.ACTION_UPDATED'));
          if (fields.includes('problem')) {
            this.refresh();
          }
          this.sendSocketUpdate();
          this.sendSocketMessage({
            team: this.action.affectTeam,
            msg: this.action.plan === 0 ? SocketMessages.CC_MSGS.PENDING : SocketMessages.CC_MSGS.ACTION_PLANS,
          });
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Force user to enter a reason if the deadline is postponed */
    onDeadlineEdit(newVal) {
      if (this.currentTeam.forceActionPostponementReason && this.$moment(newVal).isAfter(this.action.deadline)) {
        this.postponementReason = '';
        this.enteringPostponementReason = true;
        this.deadlinePendingPostponementReason = newVal;
        this.$store.dispatch(FETCH_ACTION_POSTPMT_REASONS);
        this.$nextTick(() => {
          if (this.$refs.postponementLabel) {
            this.$refs.postponementLabel.doFocus();
          }
        });
      } else {
        this.action.deadline = newVal;
        this.editAction('deadline');
      }
    },
    /** A reason for the postponement has been entered */
    postponementReasonEntered() {
      if (this.postponementReason.length > 0) {
        this.enteringPostponementReason = false;
        this.action.deadline = this.deadlinePendingPostponementReason;
        this.editAction('deadline', { deadlineHistoryComment: this.postponementReason });
      }
    },
    /** Send request to assign the action to the selected team */
    suppTeamClicked(teams) {
      if (teams == null || teams.length === 0) {
        this.chooseTeam = false;
        this.saving = false;
        this.action.plan = this.fromPlan;
        return;
      }
      this.$store.dispatch(ASSIGN_ACTION, { action: this.action, fromPlanId: this.fromPlan, supportTeamId: teams[0] })
        .then(() => {
          this.saving = false;
          this.chooseTeam = false;
          this.refresh();
          this.sendSocketUpdate();
        })
        .catch(() => {
          this.saving = false;
        });
    },
    /** If the action was assigned from another team, return the action to the previous team */
    returnAction(next) {
      const params = { id: this.action.id, team: this.action.supportData[0].fromTeam };
      this.$store.dispatch(RETURN_ACTION, params).then(() => {
        this.refresh();
        this.sendSocketUpdate();
        if (next) {
          next();
        }
      });
    },
    filterGainValue(gainValue) {
      const number = parseFloat(gainValue);
      // Set to 0 if not number or negative
      this.action.gain = (Number.isNaN(number) || number < 0 ? '0' : number.toString());
      this.editAction('gain');
    },
    /** Asign the action to a plan */
    assignAction() {
      if (this.noEdition) {
        this.action.plan = this.fromPlan;
        return;
      }
      if (this.action.id) {
        // Support action plan => assign to another team
        if (Number(this.getPlan(this.action.plan).support) === 1) {
          this.chooseTeam = true;
          return;
        }
        this.$store.dispatch(ASSIGN_ACTION, { action: this.action, fromPlanId: this.fromPlan })
          .then((data) => {
            this.fromPlan = this.action.plan;
            this.action.state = data.state;
            this.action.deadline = data.deadline;
            this.action.pdca = data.pdca;
            this.sendSocketMessage(SocketMessages.CC_MSGS.PENDING);
            this.sendSocketUpdate();
          })
          .catch(() => {
            this.value.plan = this.fromPlan;
          });
      } else {
        toastr.info(this.$t('message.SAVE_BEFORE_ASSIGN'));
      }
    },
    /** Assign the action to a problem */
    assignToProblem(problem) {
      this.action.problem = problem.id;
      this.editAction('problem');
      this.assigningToProblem = false;
    },
    save(e, callback) {
      if (e) {
        e.preventDefault();
      }
      if (this.noEdition || this.saving) {
        return;
      }
      this.saving = true;
      const params = Object.assign({ id: 0 }, this.action);
      delete params.problemId;
      delete params.problemTitle;
      delete params.causeTitle;
      delete params.plan;
      if (this.$route.query.newActionIndicators) {
        params.indicators = this.$route.query.newActionIndicators.split(',').map(id => Number(id));
      }
      this.$store.dispatch(`problem/${EDIT_ACTION}`, params).then(({ data }) => {
        if (data instanceof Object) {
          // Send socket update to problem sheet
          if (this.action.problemId > 0) {
            this.sendSocketMessage({
              socketCompany: 1,
              id: this.action.problemId,
              msg: SocketMessages.CC_MSGS.PROBLEM_SHEET,
            });
          }
          // Update content
          this.action = Object.assign({}, data);
          this.sendSocketMessage(SocketMessages.CC_MSGS.PENDING);
          if (callback instanceof Function) {
            callback(data);
          }
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
        this.saving = false;
      }).catch(() => {
        this.saving = false;
      });
    },
    /** Returns the action plan with given id */
    getPlan(id) {
      for (let i = 0, len = this.allPlans.length; i < len; i++) {
        if (Number(this.allPlans[i].id) === Number(id)) {
          return this.allPlans[i];
        }
      }
      return null;
    },
    /** Clicked 'save' btn */
    saveAndQuit() {
      this.promptBeforeLeave = false;
      globalEventDispatcher.dispatch(ShizenGlobalEvent.ACTION_CREATED);
      if (!this.action.endOfCreation) {
        this.action.endOfCreation = true;
        this.editAction('endOfCreation');
      }
      this.closeModal();
    },
    /** Deletes the action. If the action was sent from another team, return it */
    deleteAction(next) {
      if (this.isSupportAction && this.currentTeam.id === this.action.affectTeam) {
        this.returnAction(next);
        return;
      }
      toastr.info(this.$t('message.ACTION_DELETING'));
      this.$store.dispatch(`problem/${DELETE_ACTION}`, { id: this.action.id }).then(({ data }) => {
        if (data) {
          toastr.success(this.$t('message.ACTION_DELETED'));
          this.actionDeleted = true;
          this.promptBeforeLeave = false;
          this.sendSocketMessage(SocketMessages.CC_MSGS.PENDING);
          if (next) {
            next();
          }
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Delete button clicked, need confirmation */
    confirmDeleteAction() {
      if (this.noEdition) {
        calert(this.$t('message.ACTION_RESPONSIBILITY_OTHER_TEAM'));
        return;
      }
      calert(this.$t('message.CONFIRM_DELETE_ACTION'), [
        {
          title: this.$t('message.DELETE_ACTION'),
          action: () => {
            this.deleteAction(this.closeModal);
          },
          sClass: 'red',
        },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    /** Change action.state to 2 */
    complete() {
      const { plan, id } = this.action;
      this.$http.post('/server/actionPlans/complete.php', { fromPlan: plan, id }).then((response) => {
        const { data } = response;
        if (!isNaN(data) && data > 0) {
          toastr.success(this.$t('message.ACTION_COMPLETED'));
          if (this.action.problem) {
            this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.PROBLEMS_AND_PLANS, id: this.action.problem });
            this.sendSocketMessage({
              msg: SocketMessages.CC_MSGS.PROBLEM_SHEET,
              socketCompany: 1,
              id: this.action.problem,
            });
          } else {
            this.sendSocketMessage(SocketMessages.CC_MSGS.ACTION_PLANS);
          }
          if (data > 1) {
            // Then the action came from another team (as a support action). And d is the id of this team
            this.sendSocketMessage({ msg: SocketMessages.NOTIF_MSG, team: data });
          }
          this.action.state = 2;
          this.$store.dispatch(FETCH_PENDING_ACTIONS_NUMBER, true);
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Change action.state to 1 */
    uncomplete() {
      this.action.state = [0, null].includes(this.action.plan) ? 0 : 1;
      this.editAction('state');
    },
    /** Custom class of an item in the list of previous postpmt reasons */
    postpmtReasonItemClass(item) {
      const terms = this.postponementReason.toUpperCase().split(/[\s,-./']/);
      const classes = {};
      if (terms.some(term => !item.toUpperCase().includes(term))) {
        classes['postponement-reason__content__list__item--faded'] = true;
      }
      return classes;
    },
    /** A postponement reason was selected in the list */
    postpmtReasonClicked(reason) {
      this.postponementReason = reason;
      this.$nextTick(() => {
        this.$refs.postponementLabel.resizeTextarea();
      });
    },
    /** Send content change messages */
    sendSocketUpdate() {
      this.sendSocketMessage({ socketCompany: 1, id: this.action.id, msg: SocketMessages.CC_MSGS.ACTION_EDITION });
      if (this.action.problemId) {
        this.sendSocketMessage({
          socketCompany: 1,
          id: this.action.problemId,
          msg: SocketMessages.CC_MSGS.PROBLEM_SHEET,
        });
      }
    },
    createHelpSteps() {
      return [
        new IAHelpStep(null, this.$t('help.AC_INTRO')),
        new IAHelpStep(this.$refs.description, this.$t('help.AC_DESCRIPTION')),
        new IAHelpStep('.action-creation__leader', this.$t('help.AC_LEADER')),
        new IAHelpStep('.action-creation__impact', this.$t('help.AC_IMPACT')),
        // new IAHelpStep('.gain-wrapper', this.$t('help.AC_GAIN')),
        new IAHelpStep('.action-creation__deadline', this.$t('help.AC_DEADLINE')),
        new IAHelpStep(this.$refs.comments, this.$t('help.AC_COMMENTS')),
        new IAHelpStep('.action-creation__is-securing', this.$t('help.AC_SECURING')),
        new IAHelpStep('.action-creation__plans', this.$t('help.AC_PLANS')),
      ];
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.action-creation {

  .f-h-below-filters {
    padding: 0px;
  }

  &__buttons {
    background: #fff;
    height: $buttons-zone-height;
    box-sizing: border-box;
    padding: $buttons-zone-padding;
    display: flex;
    justify-content: space-between;

    > * {
      font-weight: 600;
      justify-content: center;
    }

    &__save {
      > :first-child {
        margin-right: 1rem;
      }
    }
  }

  &__wrapper {
    padding: $page-content-padding;
    box-sizing: border-box;
    height: calc(100% - #{$buttons-zone-height});
  }

  &__content-wrapper {
    background: #fff;
    border-radius: $main-border-radius;
    display: flex;
    height: 100%;
    overflow: auto;
  }

  &__column {
    flex: 0 0 33.33%;
    padding: 1rem;
    box-sizing: border-box;
    min-width: 0px;
  }

  &__pdca-wrapper {
    margin-left: 1.5rem;

    &--disabled {
      opacity: 0.5;
    }
  }

  &__sep.sep {
    margin: 1.3rem -2.86em;
    width: calc(100% + 5.72rem)
  }

  &__leader {

    .user-thumbnail {
      flex: 0 0 auto;
    }

    .user-thumbnail__avatar {
      width: 1.79rem;
      height: 1.79rem;
      font-size: 1rem;
    }

    &__button {
      margin: 0 3rem 0 1rem;
      flex: 1;
    }
  }

  &__deadline {

    &__original {
      margin-top: 0.2rem;
      font-style: italic;
      font-size: 0.85rem;
    }

    .date-field {
      width: 100%;
    }
  }

  &__impact, &__leader, &__deadline, &__plans, &__securing-state, &__problem,
  &__comments, &__hist-btn, &__causes, &__problem-link {
    margin-top: 1.5rem;
  }

  &__problem .generic-border--thin-border > div {
    min-height: 1.3rem;
  }

  &__problems-selection.modal.dark-overlay {
    .problems-list {
      h2 {
        text-align: center;
      }

      .problems {
        height: calc(100% - ((#{$h2-margin} * 2) + #{$h2-line-height}));

        &__list {
          height: 100%;
          padding: 0 1rem;
        }
      }
    }
  }

  &__problem-link {
    &.generic-border--text-btn {
      display: inline-flex;
    }
  }

  &__plans {
    margin-bottom: .4rem;

    .dropdown-component.generic-border--thin-border {
      max-width: 100%;
      background: transparent;
    }
  }

  &__securing-state {
    margin-bottom: .4rem;

    .securing-icon {
      margin-right: 1rem;
    }
  }

  &__history {
    padding: 2rem;
    box-sizing: border-box;

    &__item {
      margin-top: .7rem;
    }

    .history-item {
      margin-top: .7rem;

      .bottom-sep {
        display: none;
      }
    }

    .sep {
      margin: 2rem 0;
    }
  }

  .postponement-reason {
    padding: 1em;
    box-sizing: border-box;

    &__content {
      min-width: 30em;

      &__label {
        margin-top: 1.5em;

        .bordered-field {
          border: 2px solid #aaa;
          padding: 0.1em 0.5em;
        }
      }

      &__list {
        overflow: auto;
        height: 10em;
        margin-top: -5px;
        border: 1px solid $color-gray-border;
        border-bottom-left-radius: $high-border-radius;
        border-bottom-right-radius: $high-border-radius;

        &__item {
          line-height: 2em;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
          padding: 0 .5em;
          cursor: pointer;

          &--faded {
            opacity: 0.45;
          }

          &:hover {
            background: $color-hoverbtn-light-blue;
          }

          &:not(:last-child):after {
            content: " ";
            display: block;
            height: 1px;
            background: #c1c1c1;
            width: 100%;
            margin: auto;
          }
        }
      }
    }

    &__buttons {
      margin-top: 1.5em;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .action-creation {
    top: $smartphone-header-height;
    left: #{$smartphone-nav-width};
    height: calc(100% - #{$smartphone-header-height});
    width: calc(100% - #{$smartphone-nav-width});
  }
}

@media (max-width: $smartphone-portrait) {
  .action-creation {
    &__content-wrapper {
      flex-flow: column;
    }
  }
}

</style>
